import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  UserCheck, Search, Filter, Phone, MessageSquare, StickyNote,
  Calendar, ChevronDown, ChevronUp, X, Save, ExternalLink,
  Mail, Clock, CheckCircle2, AlertCircle, Timer, RefreshCw, Send
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pending", bg: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  quoted:  { label: "Quoted",  bg: "bg-blue-50 text-blue-700 border-blue-200" },
  proposal_sent: { label: "Proposal Sent", bg: "bg-blue-50 text-blue-700 border-blue-200" },
  approved: { label: "Approved", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  booked: { label: "Booked", bg: "bg-green-50 text-green-700 border-green-200" },
  lost: { label: "Lost", bg: "bg-red-50 text-red-700 border-red-200" },
};

const allStatuses = ["pending", "proposal_sent", "approved", "booked", "lost"];

const formatDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return `${String(dt.getDate()).padStart(2,"0")}/${String(dt.getMonth()+1).padStart(2,"0")}/${dt.getFullYear()}`;
};

const formatCurrency = (v) => {
  if (!v) return "-";
  return "₹" + Number(v).toLocaleString("en-IN");
};

// ─── Email Follow-Up Queue Tab ───────────────────────────────────────────────

function FollowUpDayPill({ day, isActive, isUpcoming, isOverdue }) {
  if (isUpcoming) return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">
      <Timer className="w-3 h-3" /> Upcoming
    </span>
  );
  if (isOverdue) return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 font-medium">
      <CheckCircle2 className="w-3 h-3" /> Window closed
    </span>
  );
  if (!isActive || !day) return null;
  const colors = ["","bg-blue-50 text-blue-600","bg-indigo-50 text-indigo-600","bg-amber-50 text-amber-600","bg-orange-50 text-orange-600","bg-red-50 text-red-600"];
  const cls = colors[day] || "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${cls}`}>
      <Mail className="w-3 h-3" /> Day {day} / 5
    </span>
  );
}

function EmailFollowUpQueue({ token }) {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [view, setView]       = useState("active"); // active | upcoming | overdue | all
  const [triggering, setTriggering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/quote-followup-window`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data || []);
    } catch {
      toast.error("Failed to load follow-up queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const triggerNow = async () => {
    setTriggering(true);
    try {
      await axios.post(`${API}/cron/trigger-quoted-followup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Follow-up email batch triggered. Check logs for details.");
    } catch {
      toast.error("Failed to trigger batch");
    } finally {
      setTriggering(false);
    }
  };

  const filtered = useMemo(() => {
    let rows = data;
    if (view === "active")   rows = data.filter(q => q.is_active);
    if (view === "upcoming") rows = data.filter(q => q.is_upcoming);
    if (view === "overdue")  rows = data.filter(q => q.is_overdue);
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(q =>
        [q.quote_number, q.agent_name, q.agent_company, q.destination, q.client_name]
          .filter(Boolean).join(" ").toLowerCase().includes(s)
      );
    }
    return rows;
  }, [data, view, search]);

  const counts = useMemo(() => ({
    active:   data.filter(q => q.is_active).length,
    upcoming: data.filter(q => q.is_upcoming).length,
    overdue:  data.filter(q => q.is_overdue).length,
    all:      data.length,
  }), [data]);

  // Stats for summary cards
  const activeByDay = useMemo(() => {
    const m = {};
    data.filter(q => q.is_active).forEach(q => {
      m[q.followup_day] = (m[q.followup_day] || 0) + 1;
    });
    return m;
  }, [data]);

  return (
    <div className="space-y-5" data-testid="email-followup-queue">

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "In Follow-up Window", val: counts.active, icon: Mail, color: "text-blue-600", bg: "bg-blue-50", ring: "ring-blue-100" },
          { label: "Upcoming (< 2 days)",  val: counts.upcoming, icon: Timer, color: "text-slate-600", bg: "bg-slate-50", ring: "ring-slate-100" },
          { label: "Window Closed",         val: counts.overdue, icon: CheckCircle2, color: "text-gray-400", bg: "bg-gray-50", ring: "ring-gray-100" },
          { label: "Total Quoted",           val: counts.all, icon: UserCheck, color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-100" },
        ].map(c => (
          <Card key={c.label} className="bg-white border-gray-200 shadow-sm" data-testid={`eq-stat-${c.label.toLowerCase().replace(/\s+/g,"-")}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${c.bg} ring-1 ${c.ring}`}>
                <c.icon className={`w-5 h-5 ${c.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{c.val}</p>
                <p className="text-xs text-gray-500 leading-tight">{c.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Follow-up day distribution */}
      {counts.active > 0 && (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Today's Email Queue — By Follow-up Day</p>
            <div className="flex flex-wrap gap-3">
              {[1,2,3,4,5].map(d => (
                <div key={d} className={`flex flex-col items-center px-5 py-3 rounded-xl border ${
                  activeByDay[d] ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200 opacity-40"
                }`} data-testid={`day-bucket-${d}`}>
                  <span className="text-xl font-bold text-gray-900">{activeByDay[d] || 0}</span>
                  <span className="text-xs text-gray-500 mt-0.5">Day {d}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* View tabs */}
        <div className="flex gap-1.5 p-1 bg-gray-100 rounded-lg">
          {[
            { key: "active",   label: "Active",       cnt: counts.active },
            { key: "upcoming", label: "Upcoming",     cnt: counts.upcoming },
            { key: "overdue",  label: "Window Closed",cnt: counts.overdue },
            { key: "all",      label: "All Quoted",   cnt: counts.all },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setView(t.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                view === t.key ? "bg-white text-blue-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`eq-view-${t.key}`}
            >
              {t.label}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                view === t.key ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-500"
              }`}>{t.cnt}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Quote #, agent, destination..."
              className="pl-8 h-9 text-sm w-56"
              data-testid="eq-search"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="gap-1.5 h-9"
            data-testid="eq-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={triggerNow}
            disabled={triggering}
            className="gap-1.5 h-9 bg-blue-600 hover:bg-blue-700"
            data-testid="eq-trigger-btn"
          >
            <Send className="w-3.5 h-3.5" />
            {triggering ? "Sending..." : "Send Now"}
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-gray-200">
          <CardContent className="py-14 text-center">
            <Mail className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No quotes in this view</p>
            <p className="text-gray-400 text-sm mt-1">
              {view === "active"
                ? "No quoted agents are currently in the 2–6 day follow-up window."
                : "Switch to 'All Quoted' to see every outstanding quote."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="eq-table">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Quote #</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Agent</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Destination</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Quoted On</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Days Since</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Email Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Amount</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(q => (
                  <tr
                    key={q.id}
                    className={`transition-colors hover:bg-slate-50/60 ${q.is_active ? "" : "opacity-60"}`}
                    data-testid={`eq-row-${q.id}`}
                  >
                    <td className="px-4 py-3">
                      <a
                        href={`/admin/quote-builder/${q.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-blue-600 text-xs font-semibold hover:underline"
                        data-testid={`eq-quote-number-${q.id}`}
                      >
                        #{q.quote_number || q.id?.slice(0,8).toUpperCase()}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800 text-sm leading-tight">{q.agent_name || "-"}</p>
                      <p className="text-gray-400 text-xs">{q.agent_company || ""}</p>
                      {q.agent_email && (
                        <p className="text-gray-400 text-xs truncate max-w-[160px]">{q.agent_email}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-800 font-medium text-sm">{q.destination || "-"}</p>
                      <p className="text-gray-400 text-xs">{formatDate(q.travel_date)}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm whitespace-nowrap">
                      {q.quoted_ref ? formatDate(q.quoted_ref) : "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {q.days_since_quoted !== null && q.days_since_quoted !== undefined ? (
                        <span className={`text-sm font-bold ${
                          q.is_active ? "text-blue-700" : q.is_upcoming ? "text-slate-500" : "text-gray-400"
                        }`}>
                          {q.days_since_quoted}d
                        </span>
                      ) : "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <FollowUpDayPill
                        day={q.followup_day}
                        isActive={q.is_active}
                        isUpcoming={q.is_upcoming}
                        isOverdue={q.is_overdue}
                      />
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">
                      {formatCurrency(q.quote_amount)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {q.agent_phone && (
                          <>
                            <a
                              href={`https://wa.me/${(q.agent_phone||"").replace(/\D/g,"")}?text=${encodeURIComponent(`Hi ${q.agent_name}, any update on quote ${q.quote_number} for ${q.destination}?`)}`}
                              target="_blank"
                              rel="noreferrer"
                              title="WhatsApp"
                              className="p-1.5 rounded-md hover:bg-green-50 text-green-500 transition-colors"
                              data-testid={`eq-whatsapp-${q.id}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </a>
                            <a
                              href={`tel:${q.agent_phone}`}
                              title="Call"
                              className="p-1.5 rounded-md hover:bg-blue-50 text-blue-500 transition-colors"
                              data-testid={`eq-call-${q.id}`}
                            >
                              <Phone className="w-4 h-4" />
                            </a>
                          </>
                        )}
                        <a
                          href={`/admin/quote-builder/${q.id}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Open Quote"
                          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                          data-testid={`eq-open-${q.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer info */}
          <div className="px-4 py-3 border-t border-gray-100 bg-slate-50 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Showing {filtered.length} quote{filtered.length !== 1 ? "s" : ""} &bull; Follow-up emails run automatically at 10 AM IST
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" /> Active window (days 2–6)</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300 inline-block" /> Upcoming / Closed</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Main AdminFollowUp Component ────────────────────────────────────────────

const AdminFollowUp = () => {
  const { token } = useAuth();

  // Top-level tab: General Follow-Up vs Email Follow-Up Queue
  const [mainTab, setMainTab] = useState("general");

  // ─ General tab state ─
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [notesModal, setNotesModal] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [notesStatus, setNotesStatus] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => { if (mainTab === "general") fetchFollowUps(); }, [mainTab]);

  const fetchFollowUps = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/follow-ups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(res.data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const uniqueAgents = useMemo(() => {
    const agents = new Map();
    quotes.forEach(q => {
      const key = q.agent_id;
      if (key && !agents.has(key)) {
        agents.set(key, `${q.agent_name || "Unknown"} (${q.agent_company || ""})`);
      }
    });
    return Array.from(agents.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [quotes]);

  const filtered = useMemo(() => {
    return quotes.filter(q => {
      if (statusFilter !== "all" && q.status !== statusFilter) return false;
      if (agentFilter !== "all" && q.agent_id !== agentFilter) return false;
      if (dateFrom && q.travel_date && q.travel_date < dateFrom) return false;
      if (dateTo && q.travel_date && q.travel_date > dateTo) return false;
      if (searchText) {
        const s = searchText.toLowerCase();
        const match = [q.quote_number, q.agent_name, q.agent_company, q.client_name, q.destination, q.client_phone]
          .filter(Boolean).join(" ").toLowerCase();
        if (!match.includes(s)) return false;
      }
      return true;
    });
  }, [quotes, statusFilter, agentFilter, dateFrom, dateTo, searchText]);

  const statusCounts = useMemo(() => {
    const counts = { all: quotes.length };
    allStatuses.forEach(s => counts[s] = 0);
    quotes.forEach(q => { if (counts[q.status] !== undefined) counts[q.status]++; });
    return counts;
  }, [quotes]);

  const openNotes = (q) => {
    setNotesModal(q);
    setNotesText(q.follow_up_notes || "");
    setFollowUpDate(q.follow_up_date || "");
    setNotesStatus(q.status || "pending");
  };

  const saveNotes = async () => {
    if (!notesModal) return;
    try {
      setSavingNotes(true);
      await axios.put(`${API}/admin/follow-ups/${notesModal.id}/notes`, {
        follow_up_notes: notesText,
        follow_up_date: followUpDate,
        status: notesStatus
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Follow-up updated!");
      setNotesModal(null);
      fetchFollowUps();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingNotes(false);
    }
  };

  const openWhatsApp = (phone, agentName, quoteNum) => {
    const cleanPhone = (phone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(`Hi ${agentName}, following up on quote ${quoteNum}. Please share an update.`);
    window.open(`https://wa.me/${cleanPhone}?text=${msg}`, "_blank");
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-5 max-w-7xl mx-auto" data-testid="admin-follow-up">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-emerald-600" />
              Follow Up
            </h1>
            <p className="text-gray-500 text-sm mt-1">Track enquiries and monitor automated email follow-up queue</p>
          </div>
          {mainTab === "general" && (
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2 border-gray-200"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
        </div>

        {/* Main Tabs */}
        <div className="flex gap-0 border-b border-gray-200" data-testid="main-tabs">
          {[
            { key: "general",  label: "General Follow-Up",    icon: StickyNote },
            { key: "email",    label: "Email Follow-up Queue", icon: Mail },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setMainTab(t.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all -mb-px ${
                mainTab === t.key
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
              data-testid={`main-tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* ── EMAIL FOLLOW-UP QUEUE TAB ── */}
        {mainTab === "email" && <EmailFollowUpQueue token={token} />}

        {/* ── GENERAL FOLLOW-UP TAB ── */}
        {mainTab === "general" && (
          <>
            {/* Status Tabs */}
            <div className="flex flex-wrap gap-2" data-testid="status-tabs">
              {[{ key: "all", label: "All" }, ...allStatuses.map(s => ({ key: s, label: statusConfig[s]?.label || s }))].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                    statusFilter === tab.key
                      ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                  data-testid={`status-tab-${tab.key}`}
                >
                  {tab.label}
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                    statusFilter === tab.key ? "bg-white/20" : "bg-gray-100"
                  }`}>
                    {statusCounts[tab.key] || 0}
                  </span>
                </button>
              ))}
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <Card className="border-gray-200 shadow-sm" data-testid="filters-panel">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-gray-600 text-xs mb-1 block">Search</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          value={searchText}
                          onChange={e => setSearchText(e.target.value)}
                          placeholder="Quote #, agent, client, destination..."
                          className="pl-9"
                          data-testid="search-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-600 text-xs mb-1 block">Agent</Label>
                      <select
                        value={agentFilter}
                        onChange={e => setAgentFilter(e.target.value)}
                        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        data-testid="agent-filter"
                      >
                        <option value="all">All Agents</option>
                        {uniqueAgents.map(([id, name]) => (
                          <option key={id} value={id}>{name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-gray-600 text-xs mb-1 block">Travel Date From</Label>
                      <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="date-from" />
                    </div>
                    <div>
                      <Label className="text-gray-600 text-xs mb-1 block">Travel Date To</Label>
                      <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="date-to" />
                    </div>
                  </div>
                  {(searchText || agentFilter !== "all" || dateFrom || dateTo) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSearchText(""); setAgentFilter("all"); setDateFrom(""); setDateTo(""); }}
                      className="mt-3 text-gray-500 hover:text-red-500 gap-1"
                      data-testid="clear-filters-btn"
                    >
                      <X className="w-3 h-3" /> Clear Filters
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            <p className="text-sm text-gray-500">{filtered.length} enquiries found</p>

            {/* Table */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : filtered.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No enquiries match your filters</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="follow-up-table">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Quote #</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Agent / Employee</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Client</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Destination</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Travel Date</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Amount</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Status</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Follow-Up</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filtered.map(q => {
                        const sCfg = statusConfig[q.status] || statusConfig.pending;
                        return (
                          <tr key={q.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`followup-row-${q.id}`}>
                            <td className="px-4 py-3">
                              <span className="font-mono text-emerald-600 text-xs font-medium" data-testid={`followup-quote-number-${q.id}`}>
                                #{q.quote_number || q.id.slice(0, 8).toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-800 text-sm">{q.agent_name || "-"}</p>
                              <p className="text-gray-400 text-xs">{q.agent_company || ""}</p>
                              {q.assigned_to_name && (
                                <p className="text-purple-500 text-xs mt-0.5">Emp: {q.assigned_to_name}</p>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-gray-800 text-sm">{q.client_name || "-"}</p>
                              {q.client_phone && <p className="text-gray-400 text-xs">{q.client_phone}</p>}
                            </td>
                            <td className="px-4 py-3 text-gray-700">{q.destination || "-"}</td>
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(q.travel_date)}</td>
                            <td className="px-4 py-3 text-right font-medium text-gray-800">{formatCurrency(q.quote_amount)}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={`${sCfg.bg} border text-xs`}>{sCfg.label}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              {q.follow_up_notes ? (
                                <div className="max-w-[180px]">
                                  <p className="text-xs text-gray-600 truncate">{q.follow_up_notes}</p>
                                  {q.follow_up_date && (
                                    <p className="text-xs text-emerald-600 mt-0.5">Next: {formatDate(q.follow_up_date)}</p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-300">No notes</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => openNotes(q)}
                                  title="Add Notes"
                                  className="p-1.5 rounded-md hover:bg-amber-50 text-amber-500 hover:text-amber-600 transition-colors"
                                  data-testid={`notes-btn-${q.id}`}
                                >
                                  <StickyNote className="w-4 h-4" />
                                </button>
                                {q.agent_phone && (
                                  <>
                                    <button
                                      onClick={() => openWhatsApp(q.agent_phone, q.agent_name, q.quote_number)}
                                      title="WhatsApp"
                                      className="p-1.5 rounded-md hover:bg-green-50 text-green-500 hover:text-green-600 transition-colors"
                                      data-testid={`whatsapp-btn-${q.id}`}
                                    >
                                      <MessageSquare className="w-4 h-4" />
                                    </button>
                                    <a
                                      href={`tel:${q.agent_phone}`}
                                      title="Call"
                                      className="p-1.5 rounded-md hover:bg-blue-50 text-blue-500 hover:text-blue-600 transition-colors"
                                      data-testid={`call-btn-${q.id}`}
                                    >
                                      <Phone className="w-4 h-4" />
                                    </a>
                                  </>
                                )}
                                <a
                                  href={`/admin/quotes/${q.id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Open Quote"
                                  className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                  data-testid={`open-quote-btn-${q.id}`}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}

        {/* Notes Modal */}
        {notesModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setNotesModal(null)}>
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-lg"
              onClick={e => e.stopPropagation()}
              data-testid="notes-modal"
            >
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <StickyNote className="w-5 h-5 text-amber-500" />
                    Follow-Up Notes
                  </h3>
                  <button onClick={() => setNotesModal(null)} className="p-1 hover:bg-gray-100 rounded">
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {notesModal.quote_number} &bull; {notesModal.agent_name} &bull; {notesModal.destination}
                </p>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <Label className="text-gray-700 text-sm">Status</Label>
                  <select
                    value={notesStatus}
                    onChange={e => setNotesStatus(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="notes-status-select"
                  >
                    {allStatuses.map(s => (
                      <option key={s} value={s}>{statusConfig[s]?.label || s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-700 text-sm">Next Follow-Up Date</Label>
                  <Input
                    type="date"
                    value={followUpDate}
                    onChange={e => setFollowUpDate(e.target.value)}
                    className="mt-1"
                    data-testid="notes-followup-date"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 text-sm">Notes / Remarks</Label>
                  <textarea
                    value={notesText}
                    onChange={e => setNotesText(e.target.value)}
                    rows={4}
                    placeholder="Add follow-up remarks..."
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
                    data-testid="notes-textarea"
                  />
                </div>
              </div>
              <div className="p-5 border-t border-gray-100 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setNotesModal(null)}>Cancel</Button>
                <Button
                  onClick={saveNotes}
                  disabled={savingNotes}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                  data-testid="save-notes-btn"
                >
                  <Save className="w-4 h-4" />
                  {savingNotes ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminFollowUp;
